import React from "react";
import { TAB_RATIO, jigsawEdges, jigsawPath, gradientCss } from "./gameUtils";

/**
 * A single jigsaw piece: a window onto the composited character square, clipped
 * to an interlocking tab/blank silhouette (mirrors iOS PuzzlePieceView). The
 * full board image rides inside a clipped box offset so this piece's cell — plus
 * a tab margin so knobs show the neighbouring picture — lands at the tile.
 */
const PuzzlePiece = ({ boardImage, composited, index, grid, tile, lifted = false }) => {
  const t = Number.isFinite(tile) && tile > 0 ? tile : 1;
  const tab = t * TAB_RATIO;
  const full = t + tab * 2;
  const boardSize = t * grid;
  const row = Math.floor(index / grid);
  const col = index % grid;
  const offX = -col * t + tab;
  const offY = -row * t + tab;
  const edges = jigsawEdges(index, grid);
  const d = jigsawPath(edges, t, tab);

  return (
    <div
      style={{
        position: "relative",
        width: full,
        height: full,
        transform: lifted ? "scale(1.1)" : "none",
        filter: lifted
          ? "drop-shadow(0 8px 12px rgba(11,26,51,0.3))"
          : "drop-shadow(0 2px 4px rgba(11,26,51,0.14))",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: `path('${d}')`,
          WebkitClipPath: `path('${d}')`,
        }}
      >
        {/* Gradient backdrop guarantees no region is ever blank when the picture
            couldn't be composited to a clean square (transparent/cropped areas). */}
        {!composited && (
          <div
            style={{
              position: "absolute",
              width: boardSize,
              height: boardSize,
              left: offX,
              top: offY,
              background: gradientCss(),
            }}
          />
        )}
        {boardImage && (
          <img
            src={boardImage}
            alt=""
            draggable={false}
            // `story-puzzle-img` + CSS vars (see index.css) force the full board
            // size past the global `img { max-width:100% !important; height:auto }`
            // rules — without it the image is clamped to the piece's own box and
            // the picture collapses into a squished strip on the left.
            className="story-puzzle-img"
            style={{
              position: "absolute",
              "--sp-img-size": `${boardSize}px`,
              left: offX,
              top: offY,
              objectFit: "cover",
              userSelect: "none",
            }}
          />
        )}
      </div>
      <svg
        width={full}
        height={full}
        viewBox={`0 0 ${full} ${full}`}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        aria-hidden
      >
        <path d={d} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth={lifted ? 2.5 : 1.5} />
        <path d={d} fill="none" stroke="rgba(0,0,0,0.10)" strokeWidth={0.8} />
      </svg>
    </div>
  );
};

export default React.memo(PuzzlePiece);
